<template>

  <v-card>
    <v-card-title v-if="title">
      <span class="headline">{{ title }}</span>
    </v-card-title>

    <v-card-text>
      <v-form>

        <slot name="description">
          <div class="mb-3 subheading" v-if="description">
            {{ description }}
          </div>
        </slot>

        <div
          v-for="(beanProperty, index) in allProperties"
          :key="beanProperty">

          <v-select
            v-if="beanPropertyValues && beanPropertyValues[beanProperty]"
            v-validate="validationRules[index]"
            v-model="model[beanProperty]"
            :ref="beanProperty"
            :error-messages="externalValidationErrors[beanProperty] || errors.collect(beanProperty)"
            :class="inputClass(beanProperty)"
            :label="defaultLabels[index]"
            :data-vv-name="beanProperty"
            :items="beanPropertyValues[beanProperty]"
            :disabled="disabledField(beanProperty)"
            @change="clearMessages"
            required
            :clearable="clearField(beanProperty)"
          ></v-select>

          <v-checkbox
            v-else-if="calculatedInputTypes[beanProperty]==='checkbox'"
            v-validate.continues="validationRules[index]"
            v-model="model[beanProperty]"
            :ref="beanProperty"
            :error-messages="externalValidationErrors[beanProperty] || errors.collect(beanProperty)"
            :class="inputClass(beanProperty)"
            :label="defaultLabels[index]"
            :data-vv-name="beanProperty"
            :disabled="disabledField(beanProperty)"
            required
          ></v-checkbox>

          <v-textarea
            v-else-if="calculatedInputTypes[beanProperty]==='textarea'"
            v-validate.continues="validationRules[index]"
            v-model="model[beanProperty]"
            :type="calculatedInputTypes[beanProperty]"
            :ref="beanProperty"
            :error-messages="externalValidationErrors[beanProperty] || errors.collect(beanProperty)"
            :class="inputClass(beanProperty)"
            :label="defaultLabels[index]"
            :data-vv-name="beanProperty"
            :disabled="disabledField(beanProperty)"
            @keypress="clearMessages"
            auto-grow
            required
          ></v-textarea>

          <v-text-field
            v-else
            v-validate.continues="validationRules[index]"
            v-model="model[beanProperty]"
            :type="calculatedInputTypes[beanProperty]"
            :ref="beanProperty"
            :error-messages="externalValidationErrors[beanProperty] || errors.collect(beanProperty)"
            :class="inputClass(beanProperty)"
            :label="defaultLabels[index]"
            :data-vv-name="beanProperty"
            :disabled="disabledField(beanProperty)"
            @keypress="clearMessages"
            required
          ></v-text-field>

        </div>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <slot name="firstActions">
      </slot>
      <v-spacer></v-spacer>
      <slot name="preActions">
      </slot>
      <v-btn flat
             :color="submitButtonColor"
             :loading="loading"
             :disabled="loading"
             @click.native="submit">
        <slot name="submitButton">
          Save
          <v-icon right dark>done</v-icon>
        </slot>
      </v-btn>
      <v-btn flat
             :color="cancelButtonColor"
             :loading="loading"
             :disabled="loading"
             @click.native="cancel">
        <slot name="cancelButton">
          Cancel
          <v-icon right dark>cancel</v-icon>
        </slot>
      </v-btn>
      <slot name="postActions">
      </slot>

    </v-card-actions>

  </v-card>
</template>

<script>

import startCase from 'lodash.startcase';

export default {
  name: 'MPayGenericEditor',
  props: {
    title: { type: String, required: false },
    description: { type: String, required: false },
    bean: { type: Object, required: true },
    beanProperties: { type: Array, required: true },
    readOnlyProperties: { type: Array, required: false, default: () => [] },
    beanPropertyValues: { type: Object, required: false },
    inputTypes: { type: Object, required: false },
    validationRules: { type: Array, required: true },
    beanLabels: { type: Array, required: false },
    submitButtonColor: { type: String, required: false, default: 'primary' },
    cancelButtonColor: { type: String, required: false, default: 'primary' },
    loading: { type: Boolean, required: false, default: false },
    clearableProperties: { type: Array, required: false, default: () => [] },
  },

  data: () => ({
    confirmation: '',
    model: {},
    originalModel: {},
  }),

  computed: {
    isFormValid() {
      return !Object.keys(this.fields)
        .some(key => this.fields[key].invalid);
    },
    isNew() {
      return !this.bean;
    },
    defaultLabels() {
      if (this.beanLabels === undefined) {
        return this.allProperties.map(p => startCase(p));
      }
      return this.beanLabels;
    },
    externalValidationErrors() {
      const result = {};
      if (this.$store.getters.validationErrors) {
        this.$store.getters.validationErrors.forEach((e) => {
          result[e.field] = e.message;
        });
        return result;
      }
      return false;
    },
    calculatedInputTypes() {
      const result = {};
      const inputTypes = this.inputTypes;
      this.beanProperties.forEach((p) => {
        if (inputTypes && inputTypes[p]) {
          result[p] = inputTypes[p];
        } else {
          result[p] = 'string';
        }
      });
      return result;
    },
    allProperties: function f() {
      return this.beanProperties.concat(this.readOnlyProperties);
    },
    disabledField() {
      return propertyName => !this.readOnlyProperties.indexOf(propertyName);
    },
    clearField() {
      return propertyName => !this.clearableProperties.indexOf(propertyName);
    },
  },

  methods: {
    submit() {
      this.$validator.validateAll()
        .then((isValid) => {
          if (isValid) {
            this.originalModel = Object.assign({}, this.model);
            this.$validator.reset();
            this.$emit('submit', Object.assign({}, this.model));
          } else {
            this.showValidationErrorMessage();
          }
        });
    },
    cancel() {
      this.model = Object.assign({}, this.originalModel);
      this.$validator.reset();
      this.$emit('cancel', this.model);
    },
    inputClass(name) {
      if (this.fields[name]) {
        const changed = this.fields[name].changed;
        return { 'beans-editor__input__not-changed': !changed };
      }
      return {};
    },
    storeModel() {
      if (this.bean) {
        this.model = Object.assign({}, this.bean);
        this.originalModel = Object.assign({}, this.bean);
        this.$validator.reset();
      } else {
        this.model = {};
      }
    },
  },

  mounted() {
    this.storeModel();
  },
  watch: {
    bean() {
      this.storeModel();
    },
    model: {
      handler() {
        this.$emit('change', Object.assign({}, this.model));
      },
      deep: true,
    },
  },

};
</script>

<style>
  .beans-editor__input__not-changed input,
  .beans-editor__input__not-changed .v-select__selection {
    color: grey !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/MPayGenericEditor.vue